import { useCallback, useEffect, useState } from 'react';
import type { WorkspaceDiffResponse } from '@reef/api-contract';
import type { WorkspaceSnapshot } from '@reef/schema';
import { request } from '../http.ts';

/**
 * What each step of a run actually changed on disk.
 *
 * The timeline says what a step *said* it did and the transcript says how it
 * reasoned; neither says which files moved. That gap is why a person reviewing
 * an agent's work has been opening the pull request to find out — and for the
 * runs that never reach a pull request, finding out at all was not possible.
 *
 * The data has been there since workspaces shipped: a snapshot per step
 * boundary, taken only when something changed. So this screen costs no new
 * writes anywhere. A step with no snapshot of its own is shown as having
 * changed nothing, which is exactly what it means, rather than being quietly
 * given its neighbour's diff.
 *
 * **Patches are fetched with the diff, not lazily per file.** A step's whole
 * change is one screen of reading and the API already bounds what it will send;
 * a request per file would turn a forty-file step into forty round trips to
 * render one card.
 */
export function WorkspaceChangesCard({ runId }: { runId: string }) {
  const [snapshots, setSnapshots] = useState<WorkspaceSnapshot[]>([]);
  const [stepId, setStepId] = useState<string>('');
  const [diff, setDiff] = useState<WorkspaceDiffResponse | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    request<{ snapshots: WorkspaceSnapshot[] }>(`/runs/${runId}/workspace/snapshots`)
      .then((result) => {
        if (!live) return;
        setSnapshots(result.snapshots);
        setError(null);
      })
      .catch((caught: Error) => live && setError(caught.message));
    return () => {
      live = false;
    };
  }, [runId]);

  const load = useCallback(
    async (step: string) => {
      setLoading(true);
      try {
        const query = step ? `?stepId=${encodeURIComponent(step)}&patch=1` : '?patch=1';
        setDiff(await request<WorkspaceDiffResponse>(`/runs/${runId}/workspace/diff${query}`));
        setError(null);
      } catch (caught) {
        setError((caught as Error).message);
      } finally {
        setLoading(false);
      }
    },
    [runId],
  );

  useEffect(() => {
    if (snapshots.length > 0) void load(stepId);
  }, [snapshots.length, stepId, load]);

  /*
   * Nothing at all is the common case — most runs never write a file — and a
   * card explaining an empty table on every one of them is noise on the screen
   * people look at most.
   */
  if (snapshots.length === 0) return null;

  const steps = [...new Set(snapshots.map((snapshot) => snapshot.stepId).filter((id): id is string => id !== null))];

  return (
    <div className="card">
      <div className="row" style={{ alignItems: 'baseline', gap: '0.75rem' }}>
        <h2 style={{ margin: 0 }}>Changes</h2>
        <select value={stepId} onChange={(event) => setStepId(event.target.value)}>
          <option value="">Whole run</option>
          {steps.map((step) => (
            <option key={step} value={step}>
              {step}
            </option>
          ))}
        </select>
        <span className="spacer" />
        {diff && (
          <span className="hint">
            {diff.filesChanged} file{diff.filesChanged === 1 ? '' : 's'} · +{diff.linesAdded} −
            {diff.linesRemoved}
          </span>
        )}
      </div>

      {error && <div className="banner danger">{error}</div>}

      {diff?.approximated && (
        <p className="hint">
          One file was too large to compare line by line, so it is reported as replaced. Its counts
          are the whole file rather than a measurement.
        </p>
      )}

      {!error && diff && diff.toSnapshotId === null && (
        <p className="hint">This step wrote nothing to the workspace.</p>
      )}

      {diff?.files.map((file) => (
        <div key={file.path} style={{ borderTop: '1px solid var(--border)', padding: '0.5rem 0' }}>
          <button
            className="ghost"
            style={{ display: 'flex', gap: '0.5rem', width: '100%', textAlign: 'left' }}
            onClick={() => setOpen(open === file.path ? null : file.path)}
          >
            <span className={`pill ${file.status === 'added' ? 'ok' : file.status === 'deleted' ? 'danger' : 'accent'}`}>
              {file.status}
            </span>
            <code>{file.path}</code>
            <span className="spacer" />
            <span className="hint">
              +{file.linesAdded} −{file.linesRemoved}
            </span>
          </button>

          {open === file.path &&
            (file.patch ? (
              <pre style={{ overflowX: 'auto', fontSize: '0.8rem' }}>{file.patch}</pre>
            ) : (
              // The reason is carried by the API rather than guessed at here: a
              // file shown with no diff and no explanation reads as a bug in
              // this screen.
              <p className="hint">
                {file.patchOmitted === 'file_too_large'
                  ? 'Too large to show a patch for.'
                  : file.patchOmitted === 'budget_exhausted'
                    ? 'Beyond this response’s patch budget — open the file to read it.'
                    : 'No patch was requested.'}
              </p>
            ))}
        </div>
      ))}

      {loading && <p className="hint">Reading the workspace…</p>}
    </div>
  );
}
